class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      this.bindFacetRadioDeselect(facetForm);
      facetForm.addEventListener('input', (event) => {
        if (event.target.matches('input[type="radio"], input[type="checkbox"]')) {
          this.onSubmitHandler(event);
          return;
        }
        this.debouncedOnSubmit(event);
      });
    }

    FacetFiltersForm.syncCollectionFilterRows();

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  bindFacetRadioDeselect(form) {
    form.addEventListener(
      'pointerdown',
      (event) => {
        const radio = FacetFiltersForm.filterRadioFromEvent(event);
        if (radio) radio.dataset.wasChecked = radio.checked ? 'true' : 'false';
      },
      true
    );

    form.addEventListener('click', (event) => {
      const radio = FacetFiltersForm.filterRadioFromEvent(event);
      if (!radio || radio.dataset.wasChecked !== 'true') return;
      radio.checked = false;
      radio.dataset.wasChecked = 'false';
      radio.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }

  static filterRadioFromEvent(event) {
    const node = event.target instanceof Element ? event.target : event.target.parentElement;
    if (!node) return null;
    const radio = node.matches('input[type="radio"]')
      ? node
      : node.closest('label')?.querySelector('input[type="radio"]');
    if (!radio || radio.disabled || !radio.name.startsWith('filter.')) return null;
    return radio;
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static syncSortByInputs(searchParams) {
    const form = document.getElementById('FacetSortForm');
    if (!form) return;

    const sortBy = new URLSearchParams(searchParams).get('sort_by') || form.dataset.defaultSort;
    if (!sortBy) return;

    form.querySelectorAll('input[name="sort_by"]').forEach((input) => {
      input.checked = input.value === sortBy;
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.syncSortByInputs(searchParams);
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCount');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    const productGridContainer = document.getElementById('ProductGridContainer');
    const collectionEl = productGridContainer?.querySelector('.collection');
    if (collectionEl) collectionEl.classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderFilterChips(html, event);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(document.getElementById('ProductGridContainer') || document);
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderFilterChips(html, event);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(document.getElementById('ProductGridContainer') || document);
    }
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderFilterChips(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const source = parsedHTML.getElementById('CollectionFilterChips');
    const target = document.getElementById('CollectionFilterChips');
    if (!source || !target) return;

    target.classList.remove('is-filter-loading');

    const changedRowIndex = FacetFiltersForm.lastFilterRowIndex;
    const isFilterChipClick = Number.isInteger(changedRowIndex) && changedRowIndex >= 0;

    if (!isFilterChipClick) {
      if (!event || event.target?.name === 'browse') {
        target.innerHTML = source.innerHTML;
      }
      FacetFiltersForm.syncCollectionFilterRows();
      return;
    }

    const sourceRows = Array.from(source.querySelectorAll('.collection-filter-chips__scroller'));
    const targetRows = FacetFiltersForm.getCollectionFilterRows();

    targetRows.forEach((row, index) => {
      if (index > changedRowIndex) row.remove();
    });

    sourceRows.forEach((row, index) => {
      if (index <= changedRowIndex) return;
      target.appendChild(document.importNode(row, true));
    });

    FacetFiltersForm.lastFilterRowIndex = -1;
    FacetFiltersForm.preservedFilterSelections = null;
    FacetFiltersForm.syncCollectionFilterRows();
  }

  static getCollectionFilterRows() {
    return Array.from(document.querySelectorAll('#CollectionFilterChips .collection-filter-chips__scroller'));
  }

  static getBrowseMode() {
    const checked = document.querySelector('.collection-browse-mode input[name="browse"]:checked');
    if (checked?.value === 'theme' || checked?.value === 'occasion') return checked.value;

    const container = document.getElementById('CollectionFilterChips');
    if (container?.dataset.browseMode === 'theme' || container?.dataset.browseMode === 'occasion') {
      return container.dataset.browseMode;
    }

    try {
      const stored = sessionStorage.getItem('twinkleBrowseMode');
      if (stored === 'theme' || stored === 'occasion') return stored;
    } catch (error) {
      // sessionStorage can be blocked
    }

    return 'occasion';
  }

  static setBrowseMode(mode) {
    const nextMode = mode === 'theme' ? 'theme' : 'occasion';
    const container = document.getElementById('CollectionFilterChips');
    if (container) container.dataset.browseMode = nextMode;

    document.querySelectorAll('.collection-browse-mode input[name="browse"]').forEach((input) => {
      input.checked = input.value === nextMode;
    });

    try {
      sessionStorage.setItem('twinkleBrowseMode', nextMode);
    } catch (error) {
      // sessionStorage can be blocked
    }
  }

  static rowBelongsToMode(row, mode) {
    const tree = row.dataset.browseTree;
    if (!tree || tree === 'shared') return true;
    return tree === mode;
  }

  static getVisibleFilterRows() {
    const mode = FacetFiltersForm.getBrowseMode();
    return FacetFiltersForm.getCollectionFilterRows().filter((row) =>
      FacetFiltersForm.rowBelongsToMode(row, mode)
    );
  }

  static resetInactiveBrowseRows() {
    const mode = FacetFiltersForm.getBrowseMode();
    FacetFiltersForm.getCollectionFilterRows().forEach((row) => {
      if (!FacetFiltersForm.rowBelongsToMode(row, mode)) {
        FacetFiltersForm.resetFilterRow(row);
        row.setAttribute('hidden', '');
      }
    });
  }

  static filterRowHasValue(row) {
    return Array.from(row.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked')).some(
      (input) => input.value !== ''
    );
  }

  static filterRowOptionCount(row) {
    return Array.from(row.querySelectorAll('input[type="radio"], input[type="checkbox"]')).filter(
      (input) => input.value !== ''
    ).length;
  }

  static filterRowHasMultipleOptions(row) {
    return FacetFiltersForm.filterRowOptionCount(row) > 1;
  }

  static resetFilterRow(row) {
    const inputs = row.querySelectorAll('input[type="radio"], input[type="checkbox"]');
    const allInput = row.querySelector('input[value=""]');
    inputs.forEach((input) => {
      input.checked = allInput ? input === allInput : false;
    });
  }

  static updateFilterAllLabels() {
    const container = document.getElementById('CollectionFilterChips');
    const rows = FacetFiltersForm.getVisibleFilterRows().filter((row) => !row.hasAttribute('hidden'));
    if (!container || !rows.length) return;

    const allText = container.dataset.filterAll || '';
    const allTemplate = container.dataset.filterAllTemplate || '';

    rows.forEach((row, index) => {
      const allFace = row.querySelector('.collection-filter-chips__all-face');
      if (!allFace) return;

      if (index === 0) {
        allFace.textContent = allText;
        return;
      }

      let parentLabel = '';
      for (let i = index - 1; i >= 0; i--) {
        if (rows[i].hasAttribute('hidden')) continue;
        const selected = rows[i].querySelector('input:checked');
        if (selected?.value) {
          parentLabel =
            selected.closest('label')?.querySelector('.collection-sort-chips__face')?.textContent.trim() || '';
          break;
        }
      }

      allFace.textContent =
        parentLabel && allTemplate ? allTemplate.replace('__LABEL__', parentLabel) : allText;
    });
  }

  static syncCollectionFilterRows() {
    const allRows = FacetFiltersForm.getCollectionFilterRows();
    if (!allRows.length) return;

    const mode = FacetFiltersForm.getBrowseMode();
    FacetFiltersForm.setBrowseMode(mode);
    FacetFiltersForm.resetInactiveBrowseRows();

    const rows = FacetFiltersForm.getVisibleFilterRows();
    let revealNext = true;
    rows.forEach((row) => {
      const useful = FacetFiltersForm.filterRowHasMultipleOptions(row);

      if (!useful) {
        row.setAttribute('hidden', '');
        FacetFiltersForm.resetFilterRow(row);
        return;
      }

      const shouldShow = revealNext;
      row.toggleAttribute('hidden', !shouldShow);

      if (!shouldShow) {
        FacetFiltersForm.resetFilterRow(row);
        revealNext = false;
        return;
      }

      revealNext = FacetFiltersForm.filterRowHasValue(row);
    });

    FacetFiltersForm.updateFilterAllLabels();
  }

  static prepareCollectionFilterRows(event) {
    const rows = FacetFiltersForm.getCollectionFilterRows();
    if (!rows.length) return;

    const activeRow = event?.target?.closest('#CollectionFilterChips .collection-filter-chips__scroller');
    if (!activeRow) return;

    const activeIndex = rows.indexOf(activeRow);
    if (activeIndex < 0) return;

    FacetFiltersForm.lastFilterRowIndex = activeIndex;

    const container = document.getElementById('CollectionFilterChips');
    if (container) container.classList.add('is-filter-loading');

    rows.forEach((row, index) => {
      if (index > activeIndex) {
        FacetFiltersForm.resetFilterRow(row);
        row.setAttribute('hidden', '');
      }
    });
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const countSource = parsedHTML.getElementById('ProductCount') || parsedHTML.getElementById('ProductCount');
    if (!countSource) return;

    const count = countSource.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCount');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const currentActiveFacetsElement = document.querySelector(selector);
      if (!activeFacetsElement || !currentActiveFacetsElement) return;
      currentActiveFacetsElement.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting', '[data-submit-label]'];
    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer')?.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams();
    const hiddenParams = new Set();
    form.querySelectorAll('#CollectionFilterChips .collection-filter-chips__scroller[hidden] [name]').forEach((input) => {
      if (input.name) hiddenParams.add(input.name);
    });
    for (const [key, value] of formData.entries()) {
      if (key === 'browse' || value === '' || hiddenParams.has(key)) continue;
      params.append(key, value);
    }
    return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (event.target?.name === 'browse') {
      FacetFiltersForm.setBrowseMode(event.target.value);
      FacetFiltersForm.lastFilterRowIndex = -1;
      FacetFiltersForm.resetInactiveBrowseRows();
      FacetFiltersForm.syncCollectionFilterRows();
    }
    FacetFiltersForm.prepareCollectionFilterRows(event);
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.syncCollectionFilterRows();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
